import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { formatCurrency, formatDate, getStatusColor, getStatusLabel, getApiErrorMessage } from "../lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Separator } from "../components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Users,
  Plus,
  MoreVertical,
  Mail,
  Phone,
  Building2,
  UserPlus,
  UserCheck,
  Eye,
  Edit,
  Trash2,
  TrendingUp,
  Percent,
  Calendar,
  Clock,
  CheckCircle,
  PhoneCall,
  MapPin,
  Video,
  Search,
  X,
  ShieldCheck,
  AlertTriangle,
} from "lucide-react";

const FOLLOWUP_TYPES = [
  { value: "llamada", label: "Llamada", icon: PhoneCall },
  { value: "email", label: "Email", icon: Mail },
  { value: "visita", label: "Visita", icon: MapPin },
  { value: "reunion", label: "Reunión", icon: Video },
];

export const CRM = () => {
  const { api, company } = useAuth();
  const [clients, setClients] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [followupDialogOpen, setFollowupDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [satValidation, setSatValidation] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    trade_name: "",  // Nombre Comercial
    reference: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    rfc: "",
    is_prospect: true,
    probability: 0,
    notes: "",
    credit_days: 0,
    // Campos SAT para CFDI
    razon_social_fiscal: "",  // Razón Social
    regimen_fiscal: "",
    uso_cfdi: "G03",
    codigo_postal_fiscal: "",
  });
  const [satCatalogs, setSatCatalogs] = useState({
    regimenes: [],
    usosCfdi: [],
  });
  const [followupForm, setFollowupForm] = useState({
    scheduled_date: "",
    scheduled_time: "10:00",
    followup_type: "llamada",
    notes: "",
  });
  const [searchFilter, setSearchFilter] = useState("");

  useEffect(() => {
    if (company?.id) {
      fetchClients();
      fetchPendingFollowups();
      fetchSatCatalogs();
    }
  }, [company]);

  const fetchSatCatalogs = async () => {
    try {
      const [regimenesRes, usosRes] = await Promise.all([
        api.get("/sat/regimen-fiscal"),
        api.get("/sat/uso-cfdi"),
      ]);
      setSatCatalogs({
        regimenes: regimenesRes.data,
        usosCfdi: usosRes.data,
      });
    } catch (error) {
      console.error("Error loading SAT catalogs:", error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get(`/clients?company_id=${company.id}`);
      setClients(response.data);
    } catch (error) {
      toast.error("Error al cargar clientes");
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingFollowups = async () => {
    try {
      const response = await api.get(`/followups/pending?company_id=${company.id}`);
      setFollowups(response.data);
    } catch (error) {
      console.error("Error fetching followups:", error);
    }
  };

  const [satError, setSatError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSatError(null); // Reset error
    try {
      // Sincronizar name con trade_name para compatibilidad
      const submitData = {
        company_id: company.id,
        ...formData,
        name: formData.trade_name || formData.name,  // name = trade_name para compatibilidad
        probability: parseInt(formData.probability) || 0,
        credit_days: parseInt(formData.credit_days) || 0,
        // Limpiar email si está vacío para evitar error de validación
        email: formData.email?.trim() || null,
      };
      
      if (editingClient) {
        const response = await api.put(`/clients/${editingClient.id}`, submitData);
        toast.success("Cliente actualizado");
        if (response.data.sat_validation?.valid) {
          toast.success("✅ Datos fiscales validados con SAT");
        }
      } else {
        const response = await api.post("/clients", submitData);
        toast.success("Cliente creado exitosamente");
        if (response.data.sat_validation?.valid) {
          toast.success("✅ Datos fiscales validados con SAT");
        }
      }
      setDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      // Handle errors - translate to clear Spanish messages
      const errDetail = error.response?.data?.detail;
      let errorMessage = "";
      
      // Parse validation errors from Pydantic
      if (Array.isArray(errDetail)) {
        // Pydantic returns array of errors
        const fieldErrors = errDetail.map(err => {
          const field = err.loc?.[1] || err.loc?.[0] || "campo";
          const fieldNames = {
            "email": "Correo Electrónico",
            "name": "Nombre",
            "trade_name": "Nombre Comercial",
            "rfc": "RFC",
            "razon_social_fiscal": "Razón Social",
            "phone": "Teléfono",
            "address": "Dirección",
            "contact_name": "Nombre de Contacto",
            "regimen_fiscal": "Régimen Fiscal",
            "codigo_postal_fiscal": "Código Postal Fiscal",
          };
          const fieldName = fieldNames[field] || field;
          
          if (err.msg?.includes("email")) {
            return `El campo "${fieldName}" debe ser un correo electrónico válido (ejemplo: correo@empresa.com)`;
          }
          return `Error en "${fieldName}": ${err.msg}`;
        });
        errorMessage = fieldErrors.join("\n");
      } else if (errDetail && typeof errDetail === 'string') {
        // Translate common errors
        if (errDetail.includes("email") && errDetail.includes("@")) {
          errorMessage = "El campo Correo Electrónico no es válido. Debe incluir @ (ejemplo: correo@empresa.com) o déjalo vacío.";
        } else if (errDetail.includes("validación SAT")) {
          errorMessage = errDetail;
          setSatValidation({ valid: false, error: errDetail });
        } else {
          errorMessage = errDetail;
        }
      } else if (errDetail && typeof errDetail === 'object' && errDetail.validation) {
        errorMessage = errDetail.message;
        setSatValidation(errDetail.validation);
      } else {
        errorMessage = getApiErrorMessage(error, "Error al guardar cliente");
      }
      
      setSatError(errorMessage);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setSatValidation(null); // Reset validation state
    setFormData({
      name: client.name,
      trade_name: client.trade_name || client.name || "",  // Nombre Comercial
      reference: client.reference || "",
      contact_name: client.contact_name || "",
      email: client.email || "",
      phone: client.phone || "",
      address: client.address || "",
      rfc: client.rfc || "",
      is_prospect: client.is_prospect,
      probability: client.probability || 0,
      notes: client.notes || "",
      credit_days: client.credit_days || 0,
      razon_social_fiscal: client.razon_social_fiscal || "",
      regimen_fiscal: client.regimen_fiscal || "",
      uso_cfdi: client.uso_cfdi || "G03",
      codigo_postal_fiscal: client.codigo_postal_fiscal || "",
    });
    // Show if client was previously validated
    if (client.sat_validated) {
      setSatValidation({ valid: true, rfc_exists: true, match_name: true });
    }
    setDialogOpen(true);
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm("¿Estás seguro de eliminar este cliente?")) return;
    try {
      await api.delete(`/clients/${clientId}`);
      toast.success("Cliente eliminado");
      fetchClients();
    } catch (error) {
      toast.error("Error al eliminar cliente");
    }
  };

  const handleConvertToClient = async (client) => {
    // Check if client has required fiscal data
    if (!client.rfc || !client.razon_social_fiscal) {
      toast.error(
        <div>
          <p className="font-medium">Datos fiscales incompletos</p>
          <p className="text-sm">Para convertir a cliente, agrega RFC y Razón Social primero.</p>
        </div>,
        { duration: 5000 }
      );
      // Open edit dialog to complete data
      handleEdit(client);
      return;
    }
    
    try {
      const response = await api.put(`/clients/${client.id}`, {
        ...client,
        is_prospect: false,
        probability: 100,
      });
      toast.success("Prospecto convertido a cliente");
      if (response.data.sat_validation?.valid) {
        toast.success("✅ Datos fiscales validados con SAT");
      }
      fetchClients();
    } catch (error) {
      // Handle SAT validation errors
      const errData = error.response?.data?.detail;
      if (errData && typeof errData === 'object' && errData.validation) {
        toast.error(
          <div className="space-y-1">
            <p className="font-medium">No se puede convertir a cliente</p>
            <p className="text-sm">{errData.message}</p>
            <p className="text-xs opacity-80">Edita el cliente y corrige los datos fiscales</p>
          </div>,
          { duration: 8000 }
        );
        // Open edit dialog to fix data
        handleEdit(client);
      } else {
        toast.error("Error al convertir prospecto");
      }
    }
  };

  const handleCreateFollowup = async (e) => {
    e.preventDefault();
    if (!selectedClient) return;
    try {
      const scheduledDateTime = `${followupForm.scheduled_date}T${followupForm.scheduled_time}:00`;
      await api.post(`/clients/${selectedClient.id}/followups`, {
        company_id: company.id,
        client_id: selectedClient.id,
        scheduled_date: scheduledDateTime,
        followup_type: followupForm.followup_type,
        notes: followupForm.notes,
      });
      toast.success("Seguimiento programado");
      setFollowupDialogOpen(false);
      resetFollowupForm();
      fetchPendingFollowups();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error al crear seguimiento"));
    }
  };

  const handleCompleteFollowup = async (followupId, result) => {
    try {
      await api.put(`/followups/${followupId}`, {
        status: "completed",
        result: result || "Completado",
      });
      toast.success("Seguimiento completado");
      fetchPendingFollowups();
    } catch (error) {
      toast.error("Error al completar seguimiento");
    }
  };

  const resetForm = () => {
    setEditingClient(null);
    setSatValidation(null);
    setFormData({
      name: "",
      trade_name: "",
      reference: "",
      contact_name: "",
      email: "",
      phone: "",
      address: "",
      rfc: "",
      is_prospect: true,
      probability: 0,
      notes: "",
      credit_days: 0,
      razon_social_fiscal: "",
      regimen_fiscal: "",
      uso_cfdi: "G03",
      codigo_postal_fiscal: "",
    });
    setSatError(null);
  };

  const resetFollowupForm = () => {
    setSelectedClient(null);
    setFollowupForm({
      scheduled_date: "",
      scheduled_time: "10:00",
      followup_type: "llamada",
      notes: "",
    });
  };

  const openFollowupDialog = (client) => {
    setSelectedClient(client);
    // Set default date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setFollowupForm(prev => ({
      ...prev,
      scheduled_date: tomorrow.toISOString().split('T')[0],
    }));
    setFollowupDialogOpen(true);
  };

  // Safe filter function that handles null/undefined values
  const safeFilter = (arr, predicate) => {
    try {
      return Array.isArray(arr) ? arr.filter(predicate) : [];
    } catch (e) {
      console.error("Filter error:", e);
      return [];
    }
  };

  // Safe string search
  const safeIncludes = (str, search) => {
    try {
      return str && typeof str === 'string' ? str.toLowerCase().includes(search) : false;
    } catch (e) {
      return false;
    }
  };

  // First filter by tab, then by search
  const tabFilteredClients = safeFilter(clients, (c) => {
    if (!c) return false;
    if (activeTab === "prospects") return c.is_prospect === true;
    if (activeTab === "clients") return c.is_prospect === false;
    return true;
  });
  
  const filteredClients = safeFilter(tabFilteredClients, (client) => {
    if (!client) return false;
    if (!searchFilter) return true;
    const search = searchFilter.toLowerCase();
    return (
      safeIncludes(client.name, search) ||
      safeIncludes(client.trade_name, search) ||
      safeIncludes(client.razon_social_fiscal, search) ||
      safeIncludes(client.reference, search) ||
      safeIncludes(client.contact_name, search) ||
      safeIncludes(client.email, search) ||
      safeIncludes(client.phone, search) ||
      safeIncludes(client.rfc, search) ||
      safeIncludes(client.address, search)
    );
  });

  const stats = {
    total: Array.isArray(clients) ? clients.length : 0,
    prospects: safeFilter(clients, (c) => c && c.is_prospect === true).length,
    clients: safeFilter(clients, (c) => c && c.is_prospect === false).length,
    highProbability: safeFilter(clients, (c) => c && c.is_prospect === true && c.probability >= 60).length,
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="crm-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-[Chivo] text-slate-900">CRM Comercial</h1>
          <p className="text-muted-foreground">Gestión de clientes y prospectos</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-industrial" data-testid="add-client-btn">
              <Plus className="mr-2 h-4 w-4" />
              Nuevo Cliente/Prospecto
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle>{editingClient ? "Editar" : "Nuevo"} Cliente/Prospecto</DialogTitle>
                <DialogDescription>
                  Ingresa los datos del contacto comercial
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-3 py-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2 grid gap-2">
                    <Label htmlFor="trade_name" className="flex items-center gap-1">
                      Nombre Comercial <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="trade_name"
                      value={formData.trade_name}
                      onChange={(e) => setFormData({ ...formData, trade_name: e.target.value })}
                      placeholder="MANTENIMIENTO INDUSTRIAL ALAMO"
                      required
                      data-testid="client-trade-name-input"
                    />
                    <p className="text-xs text-muted-foreground">Nombre con el que se conoce comercialmente</p>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="reference">Referencia</Label>
                    <Input
                      id="reference"
                      value={formData.reference}
                      onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                      placeholder="Sucursal Norte"
                      title="Campo para diferenciar clientes"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="razon_social_fiscal">Razón Social (SAT)</Label>
                    <Input
                      id="razon_social_fiscal"
                      value={formData.razon_social_fiscal}
                      onChange={(e) => {
                        setFormData({ ...formData, razon_social_fiscal: e.target.value.toUpperCase() });
                        setSatValidation(null);
                        setSatError(null); // Clear error on change
                      }}
                      placeholder="MARISELA VAZQUEZ GARCIA"
                      className={satError && satError.includes("nombre") ? "border-red-500 border-2" : satValidation?.match_name === false ? "border-red-500" : satValidation?.match_name === true ? "border-green-500" : ""}
                    />
                    <p className="text-xs text-muted-foreground">Exactamente como aparece en la Constancia de Situación Fiscal del SAT (con acentos y Ñ)</p>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="rfc">RFC</Label>
                    <Input
                      id="rfc"
                      value={formData.rfc}
                      onChange={(e) => {
                        setFormData({ ...formData, rfc: e.target.value.toUpperCase() });
                        setSatValidation(null);
                        setSatError(null); // Clear error on change
                      }}
                      placeholder="VAGM570107AP6"
                      className={satError && satError.includes("RFC") ? "border-red-500 border-2" : satValidation?.rfc_exists === false ? "border-red-500" : satValidation?.rfc_exists === true ? "border-green-500" : ""}
                    />
                  </div>
                </div>

                {/* SAT Validation Error Display - Prominente dentro del modal */}
                {satError && (
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-red-50 border-2 border-red-300 text-sm animate-pulse">
                    <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="text-red-700 flex-1">
                      <p className="font-bold text-red-800 mb-1">⚠️ Error de Validación SAT</p>
                      <p className="mb-2">{satError}</p>
                      <p className="text-xs bg-red-100 p-2 rounded border border-red-200">
                        💡 <strong>Tip:</strong> Verifica que el RFC y la Razón Social sean EXACTAMENTE iguales a la Constancia de Situación Fiscal del SAT (incluyendo acentos, Ñ, espacios y mayúsculas).
                      </p>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="contact_name">Nombre de Contacto</Label>
                    <Input
                      id="contact_name"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      placeholder="Juan Pérez"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="email">Correo Electrónico</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="contacto@empresa.com"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="phone">Teléfono</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+52 55 1234 5678"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="address">Dirección</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Av. Principal 123, Ciudad"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label>Tipo</Label>
                    <select
                      value={formData.is_prospect ? "prospect" : "client"}
                      onChange={(e) => setFormData({ ...formData, is_prospect: e.target.value === "prospect" })}
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      <option value="prospect">Prospecto</option>
                      <option value="client">Cliente</option>
                    </select>
                  </div>
                  {formData.is_prospect ? (
                    <div className="grid gap-2">
                      <Label htmlFor="probability">Probabilidad de Cierre (%)</Label>
                      <Input
                        id="probability"
                        type="number"
                        min="0"
                        max="100"
                        value={formData.probability}
                        onChange={(e) => setFormData({ ...formData, probability: e.target.value })}
                        placeholder="50"
                      />
                    </div>
                  ) : (
                    <div className="grid gap-2">
                      <Label htmlFor="credit_days">Plazo de Crédito (días)</Label>
                      <Input
                        id="credit_days"
                        type="number"
                        min="0"
                        value={formData.credit_days}
                        onChange={(e) => setFormData({ ...formData, credit_days: parseInt(e.target.value) || 0 })}
                        placeholder="30"
                      />
                    </div>
                  )}
                </div>
                
                {/* Datos Fiscales SAT */}
                {!formData.is_prospect && (
                  <>
                    <Separator className="my-2" />
                    <p className="text-sm font-medium text-muted-foreground">Datos Fiscales Adicionales (CFDI)</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label>Código Postal Fiscal</Label>
                        <Input
                          value={formData.codigo_postal_fiscal}
                          onChange={(e) => setFormData({ ...formData, codigo_postal_fiscal: e.target.value })}
                          placeholder="12345"
                          maxLength={5}
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label>Régimen Fiscal</Label>
                        <select
                          value={formData.regimen_fiscal}
                          onChange={(e) => setFormData({ ...formData, regimen_fiscal: e.target.value })}
                          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        >
                          <option value="">Seleccionar...</option>
                          {satCatalogs.regimenes.map((r) => (
                            <option key={r.clave} value={r.clave}>
                              {r.clave} - {r.descripcion.substring(0, 40)}...
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="grid gap-2">
                        <Label>Uso CFDI</Label>
                        <select
                          value={formData.uso_cfdi}
                          onChange={(e) => setFormData({ ...formData, uso_cfdi: e.target.value })}
                          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        >
                          <option value="">Seleccionar...</option>
                          {satCatalogs.usosCfdi.map((u) => (
                            <option key={u.clave} value={u.clave}>
                              {u.clave} - {u.descripcion.substring(0, 35)}...
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </>
                )}

                <div className="grid gap-2">
                  <Label htmlFor="notes">Notas</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Notas adicionales..."
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="btn-industrial" data-testid="save-client-btn">
                  {editingClient ? "Actualizar" : "Crear"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="stat-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Contactos</CardTitle>
            <Users className="h-5 w-5 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-[Chivo]">{stats.total}</div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Prospectos</CardTitle>
            <UserPlus className="h-5 w-5 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-[Chivo] text-amber-600">{stats.prospects}</div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Clientes Activos</CardTitle>
            <UserCheck className="h-5 w-5 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-[Chivo] text-emerald-600">{stats.clients}</div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Alta Probabilidad</CardTitle>
            <TrendingUp className="h-5 w-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-[Chivo] text-blue-600">{stats.highProbability}</div>
          </CardContent>
        </Card>
      </div>

      {/* Clients Table */}
      <Card data-testid="clients-table-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Directorio Comercial
              </CardTitle>
              <CardDescription>
                {filteredClients.length} contacto(s) encontrado(s)
              </CardDescription>
            </div>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">Todos</TabsTrigger>
                <TabsTrigger value="prospects">Prospectos</TabsTrigger>
                <TabsTrigger value="clients">Clientes</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search Filter */}
          <div className="flex items-center gap-2 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre, email, teléfono, RFC..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                className="pl-9"
                data-testid="crm-search-filter"
              />
              {searchFilter && (
                <button
                  onClick={() => setSearchFilter("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {searchFilter && (
              <Badge variant="secondary" className="flex items-center gap-1">
                Filtro activo: "{searchFilter}"
              </Badge>
            )}
          </div>
          <div className="rounded-sm border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Nombre Comercial</TableHead>
                  <TableHead>Razón Social / RFC</TableHead>
                  <TableHead>Contacto</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Probabilidad</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No hay contactos registrados
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredClients.map((client, index) => (
                    client && (
                    <TableRow key={client.id || `client-${index}`} data-testid={`client-row-${client.id || index}`}>
                      <TableCell>
                        <div className="font-semibold text-primary">{client.trade_name || client.name || "Sin nombre"}</div>
                        {client.reference && (
                          <div className="text-xs text-blue-600">{client.reference}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        {client.razon_social_fiscal && (
                          <div className="text-sm flex items-center gap-1">
                            {client.razon_social_fiscal}
                            {client.sat_validated && (
                              <ShieldCheck className="h-3 w-3 text-green-600" title="Validado SAT" />
                            )}
                          </div>
                        )}
                        {client.rfc && (
                          <div className="text-xs text-muted-foreground font-mono">{client.rfc}</div>
                        )}
                        {!client.razon_social_fiscal && !client.rfc && "-"}
                      </TableCell>
                      <TableCell>{client.contact_name || "-"}</TableCell>
                      <TableCell>
                        {client.email ? (
                          <a href={`mailto:${client.email}`} className="text-primary hover:underline text-sm">
                            {client.email}
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={client.is_prospect ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}>
                          {client.is_prospect ? "Prospecto" : "Cliente"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {client.is_prospect ? (
                          <div className="flex items-center gap-1">
                            <Percent className="h-3 w-3" />
                            <span className={client.probability >= 60 ? "text-emerald-600 font-medium" : ""}>
                              {client.probability || 0}%
                            </span>
                          </div>
                        ) : (
                          <span className="text-emerald-600">100%</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(client)}>
                              <Edit className="mr-2 h-4 w-4" />
                              Editar
                            </DropdownMenuItem>
                            {client.is_prospect && (
                              <>
                                <DropdownMenuItem onClick={() => openFollowupDialog(client)}>
                                  <Calendar className="mr-2 h-4 w-4 text-blue-500" />
                                  Programar Seguimiento
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleConvertToClient(client)}>
                                  <UserCheck className="mr-2 h-4 w-4 text-emerald-500" />
                                  Convertir a Cliente
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDelete(client.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                    )
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pending Followups Section */}
      {followups.length > 0 && (
        <Card data-testid="followups-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="h-5 w-5 text-blue-500" />
              Seguimientos Pendientes ({followups.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {safeFilter(followups, f => f).map((f, index) => {
                if (!f) return null;
                const FollowupIcon = FOLLOWUP_TYPES.find(t => t.value === f.followup_type)?.icon || Phone;
                const scheduledDate = f.scheduled_date ? new Date(f.scheduled_date) : new Date();
                const isToday = scheduledDate.toDateString() === new Date().toDateString();
                const isPast = scheduledDate < new Date();
                
                return (
                  <div 
                    key={f.id || `followup-${index}`} 
                    className={`p-3 border rounded-lg flex items-center justify-between ${
                      isPast ? "border-red-200 bg-red-50" : isToday ? "border-amber-200 bg-amber-50" : ""
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${isPast ? "bg-red-100" : isToday ? "bg-amber-100" : "bg-blue-100"}`}>
                        <FollowupIcon className={`h-4 w-4 ${isPast ? "text-red-600" : isToday ? "text-amber-600" : "text-blue-600"}`} />
                      </div>
                      <div>
                        <div className="font-medium">{f.client_name}</div>
                        <div className="text-sm text-muted-foreground flex items-center gap-2">
                          <Calendar className="h-3 w-3" />
                          {formatDate(f.scheduled_date)}
                          {f.notes && <span>• {f.notes}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {f.client_phone && (
                        <Button variant="ghost" size="sm" asChild>
                          <a href={`tel:${f.client_phone}`}>
                            <Phone className="h-4 w-4" />
                          </a>
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleCompleteFollowup(f.id, "Contactado")}
                      >
                        <CheckCircle className="mr-1 h-3 w-3" />
                        Completar
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Followup Dialog */}
      <Dialog open={followupDialogOpen} onOpenChange={setFollowupDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <form onSubmit={handleCreateFollowup}>
            <DialogHeader>
              <DialogTitle>Programar Seguimiento</DialogTitle>
              <DialogDescription>
                {selectedClient?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Fecha</Label>
                  <Input
                    type="date"
                    value={followupForm.scheduled_date}
                    onChange={(e) => setFollowupForm({ ...followupForm, scheduled_date: e.target.value })}
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Hora</Label>
                  <Input
                    type="time"
                    value={followupForm.scheduled_time}
                    onChange={(e) => setFollowupForm({ ...followupForm, scheduled_time: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label>Tipo de Seguimiento</Label>
                <select
                  value={followupForm.followup_type}
                  onChange={(e) => setFollowupForm({ ...followupForm, followup_type: e.target.value })}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  {FOLLOWUP_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid gap-2">
                <Label>Notas</Label>
                <Textarea
                  value={followupForm.notes}
                  onChange={(e) => setFollowupForm({ ...followupForm, notes: e.target.value })}
                  placeholder="Motivo del seguimiento..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFollowupDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="btn-industrial">
                Programar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CRM;
